/**
 * =============================================================================
 * ManageVisibilityDialog - Diálogo para gerenciar a visibilidade de uma entidade
 * Versão: 1.0.0 (2026-06-25)
 *
 * Envolve o componente VisibilitySelector em um Dialog padrão e cuida do
 * carregamento (get_entity_visibility) e da gravação (set_entity_visibility).
 *
 * Uso típico (a partir de um item de menu "Visibilidade"):
 *
 *   <ManageVisibilityDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     entityType="campaign_list"
 *     entityId={list.id}
 *     entityLabel={list.name}
 *   />
 *
 * Permite cobrir as 3 entidades (Listas, Campanhas, Templates) sem replicar
 * código nas páginas.
 * =============================================================================
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import VisibilitySelector from '@/components/shared/VisibilitySelector';
import {
  useEntityVisibility,
  useSetEntityVisibility,
  type EntityVisibility,
  type VisibilityEntityType,
} from '@/hooks/useEntityVisibility';

interface ManageVisibilityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: VisibilityEntityType;
  entityId: string | null | undefined;
  /** Nome do registro exibido no cabeçalho (ex.: nome da lista). */
  entityLabel?: string;
}

const ENTITY_TITLE: Record<VisibilityEntityType, string> = {
  campaign_list: 'Visibilidade da lista',
  campaign: 'Visibilidade da campanha',
  whatsapp_template: 'Visibilidade do template',
  task_checklist_template: 'Visibilidade do template de checklist',
};

export default function ManageVisibilityDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  entityLabel,
}: ManageVisibilityDialogProps) {
  const { data, isLoading } = useEntityVisibility({ entityType, entityId, enabled: open });
  const setMutation = useSetEntityVisibility();

  // Estado local controlado, sincronizado quando o data chega
  const [visibility, setVisibility] = useState<EntityVisibility>('public');
  const [users, setUsers] = useState<string[]>([]);
  const [profiles, setProfiles] = useState<string[]>([]);

  useEffect(() => {
    if (open && data) {
      setVisibility(data.visibility);
      setUsers(data.allowed_user_ids);
      setProfiles(data.allowed_profile_ids);
    }
  }, [open, data]);

  const handleSave = async () => {
    if (!entityId) return;
    await setMutation.mutateAsync({
      entityType,
      entityId,
      visibility,
      allowedUserIds: users,
      allowedProfileIds: profiles,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{ENTITY_TITLE[entityType]}</DialogTitle>
          <DialogDescription>
            {entityLabel
              ? `Defina quem poderá ver "${entityLabel}".`
              : 'Defina quem poderá ver e usar este registro.'}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Carregando configuração…</span>
          </div>
        ) : (
          <VisibilitySelector
            value={visibility}
            onChange={setVisibility}
            allowedUserIds={users}
            onAllowedUserIdsChange={setUsers}
            allowedProfileIds={profiles}
            onAllowedProfileIdsChange={setProfiles}
            hideTitle
            disabled={setMutation.isPending}
          />
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={setMutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isLoading || setMutation.isPending}>
            {setMutation.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
